import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useApp } from '@/components/AppLayout';
import {
  paymentApi,
  quoteApi,
  type OutstandingInvoice,
  type ClientOutstandingResponse,
  type Quote,
  type PaymentMethod,
  type PaymentMethodChoice,
} from '@/lib/api';
import { getInvoiceStatusConfig } from '@/lib/invoiceStatus';
import { getQuoteStatusConfig } from '@/lib/quoteStatus';
import { formatAmount } from '@/lib/currency';
import { DollarSign, User, ArrowLeft, CalendarIcon, Receipt, FileText } from 'lucide-react';
import { format } from 'date-fns';

// Currency input component for payment amounts
function CurrencyInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/[^0-9.]/g, '');
    // Only allow one decimal point and two decimal places
    const parts = input.split('.');
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 2) return;
    onChange(input);
  };

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
        $
      </span>
      <Input
        type="text"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        className="pl-7 text-right h-9"
        placeholder="0.00"
      />
    </div>
  );
}

// Quote with deposit due - derived from Quote type
interface QuoteWithDeposit {
  id: string;
  quote_number: string;
  client: string | null;
  client_address: string;
  status: Quote['status'];
  deposit: string; // Deposit required
  amount_paid: string; // Amount collected
  balance_due: string; // Deposit balance due
}

function CollectPaymentContent() {
  const { nav, navigate } = useBusinessNav();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentBusiness } = useApp();

  // Get client, invoice, and quote IDs from URL params
  const clientIdParam = searchParams.get('client_id');
  const invoiceIdParam = searchParams.get('invoice_id');
  const quoteIdParam = searchParams.get('quote_id');

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [clientData, setClientData] = useState<ClientOutstandingResponse | null>(null);
  const [quotesWithDeposits, setQuotesWithDeposits] = useState<QuoteWithDeposit[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodChoice[]>([]);

  // Payment form state
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('other');
  const [reference, setReference] = useState('');
  const [transactionDate, setTransactionDate] = useState<Date>(new Date());
  const [details, setDetails] = useState('');

  // Invoice selection and amounts
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [invoiceAmounts, setInvoiceAmounts] = useState<Record<string, string>>({});

  // Quote selection and amounts
  const [selectedQuotes, setSelectedQuotes] = useState<Set<string>>(new Set());
  const [quoteAmounts, setQuoteAmounts] = useState<Record<string, string>>({});

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle case where only quote_id is provided - fetch quote to get client_id
  useEffect(() => {
    if (!clientIdParam && quoteIdParam) {
      quoteApi.get(quoteIdParam).then((response) => {
        if (response.data && response.data.client) {
          // Redirect to same page with client_id added
          setSearchParams({
            client_id: response.data.client,
            quote_id: quoteIdParam,
          }, { replace: true });
        } else if (response.error) {
          setError(response.error);
          setLoading(false);
        }
      });
    }
  }, [clientIdParam, quoteIdParam, setSearchParams]);

  // Load data when we have client_id
  useEffect(() => {
    if (!clientIdParam || !currentBusiness) {
      if (!quoteIdParam) {
        // Only show error if we don't have quote_id (which will redirect)
        setError('No client specified');
        setLoading(false);
      }
      return;
    }

    // Load outstanding invoices and quotes in parallel
    Promise.all([
      paymentApi.getClientOutstanding(clientIdParam),
      quoteApi.list(currentBusiness.id, [clientIdParam]),
      paymentApi.getPaymentMethods(),
    ]).then(([invoicesResponse, quotesResponse, methodsResponse]) => {
      // Handle invoices
      if (invoicesResponse.data) {
        setClientData(invoicesResponse.data);

        // If invoice_id is provided, pre-select it and populate amount
        if (invoiceIdParam) {
          const invoice = invoicesResponse.data.outstanding_invoices.find(
            (inv) => inv.id === invoiceIdParam
          );
          if (invoice) {
            setSelectedInvoices(new Set([invoiceIdParam]));
            setInvoiceAmounts({ [invoiceIdParam]: invoice.balance_due });
          }
        }
      } else if (invoicesResponse.error) {
        setError(invoicesResponse.error);
      }

      // Handle quotes - filter for those with deposit balance > 0
      if (quotesResponse.data) {
        const quotesWithDepositsDue = quotesResponse.data.results
          .filter((quote) => {
            const deposit = parseFloat(quote.deposit || '0');
            const depositFullyPaid = quote.deposit_fully_paid;
            const balanceDue = parseFloat(quote.balance_due || '0');
            // Show quotes that have a deposit required and it's not fully paid
            return deposit > 0 && !depositFullyPaid && balanceDue > 0 &&
              ['awaiting_response', 'awaiting_deposit', 'approved'].includes(quote.status);
          })
          .map((quote): QuoteWithDeposit => ({
            id: quote.id,
            quote_number: quote.quote_number,
            client: quote.client,
            client_address: quote.client_address,
            status: quote.status,
            deposit: quote.deposit,
            amount_paid: quote.amount_paid,
            balance_due: quote.balance_due,
          }));

        setQuotesWithDeposits(quotesWithDepositsDue);

        // If quote_id is provided, pre-select it and populate amount
        if (quoteIdParam) {
          const quote = quotesWithDepositsDue.find((q) => q.id === quoteIdParam);
          if (quote) {
            setSelectedQuotes(new Set([quoteIdParam]));
            setQuoteAmounts({ [quoteIdParam]: quote.balance_due });
          }
        }
      }

      // Handle payment methods
      if (methodsResponse.data) {
        // Filter out credit_memo as it's auto-generated
        setPaymentMethods(methodsResponse.data.filter(([value]) => value !== 'credit_memo'));
      }

      setLoading(false);
    });
  }, [clientIdParam, invoiceIdParam, quoteIdParam, currentBusiness]);

  // Calculate total payment amount from selected invoices and quotes
  const totalPaymentAmount = useMemo(() => {
    let total = 0;
    selectedInvoices.forEach((invoiceId) => {
      const amount = parseFloat(invoiceAmounts[invoiceId] || '0');
      if (!isNaN(amount)) {
        total += amount;
      }
    });
    selectedQuotes.forEach((quoteId) => {
      const amount = parseFloat(quoteAmounts[quoteId] || '0');
      if (!isNaN(amount)) {
        total += amount;
      }
    });
    return total;
  }, [selectedInvoices, invoiceAmounts, selectedQuotes, quoteAmounts]);

  // Handle invoice selection toggle
  const handleInvoiceToggle = (invoiceId: string, invoice: OutstandingInvoice) => {
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(invoiceId)) {
      newSelected.delete(invoiceId);
      // Clear the amount when deselected
      const newAmounts = { ...invoiceAmounts };
      delete newAmounts[invoiceId];
      setInvoiceAmounts(newAmounts);
    } else {
      newSelected.add(invoiceId);
      // Set default amount to balance due
      setInvoiceAmounts({
        ...invoiceAmounts,
        [invoiceId]: invoice.balance_due,
      });
    }
    setSelectedInvoices(newSelected);
  };

  // Handle quote selection toggle
  const handleQuoteToggle = (quoteId: string, quote: QuoteWithDeposit) => {
    const newSelected = new Set(selectedQuotes);
    if (newSelected.has(quoteId)) {
      newSelected.delete(quoteId);
      // Clear the amount when deselected
      const newAmounts = { ...quoteAmounts };
      delete newAmounts[quoteId];
      setQuoteAmounts(newAmounts);
    } else {
      newSelected.add(quoteId);
      // Set default amount to balance due (deposit balance)
      setQuoteAmounts({
        ...quoteAmounts,
        [quoteId]: quote.balance_due,
      });
    }
    setSelectedQuotes(newSelected);
  };

  // Handle amount change for an invoice
  const handleInvoiceAmountChange = (invoiceId: string, value: string) => {
    setInvoiceAmounts({
      ...invoiceAmounts,
      [invoiceId]: value,
    });
  };

  // Handle amount change for a quote
  const handleQuoteAmountChange = (quoteId: string, value: string) => {
    setQuoteAmounts({
      ...quoteAmounts,
      [quoteId]: value,
    });
  };

  // Deselect all
  const handleDeselectAll = () => {
    setSelectedInvoices(new Set());
    setInvoiceAmounts({});
    setSelectedQuotes(new Set());
    setQuoteAmounts({});
  };

  // Handle save
  const handleSave = async (_sendReceipt: boolean = false) => {
    if (!currentBusiness || !clientIdParam) return;

    // Validate
    if (selectedInvoices.size === 0 && selectedQuotes.size === 0) {
      setError('Please select at least one invoice or quote');
      return;
    }

    if (totalPaymentAmount <= 0) {
      setError('Payment amount must be greater than zero');
      return;
    }

    setSaving(true);
    setError('');

    // Build allocations for both invoices and quotes
    const allocations = [
      ...Array.from(selectedInvoices).map((invoiceId) => ({
        invoice: invoiceId,
        amount: invoiceAmounts[invoiceId] || '0',
      })),
      ...Array.from(selectedQuotes).map((quoteId) => ({
        quote: quoteId,
        amount: quoteAmounts[quoteId] || '0',
      })),
    ];

    const response = await paymentApi.create({
      business: currentBusiness.id,
      client: clientIdParam,
      amount: totalPaymentAmount.toFixed(2),
      payment_date: format(transactionDate, 'yyyy-MM-dd'),
      payment_method: paymentMethod,
      reference,
      notes: details,
      allocations,
    });

    if (response.data) {
      // TODO: If sendReceipt, send email receipt
      // Navigate based on what was paid
      const totalDocuments = selectedInvoices.size + selectedQuotes.size;

      if (totalDocuments === 1) {
        // Single document - go to its view page
        if (selectedInvoices.size === 1) {
          const invoiceId = Array.from(selectedInvoices)[0];
          nav(`/invoices/${invoiceId}`);
        } else {
          const quoteId = Array.from(selectedQuotes)[0];
          nav(`/quotes/${quoteId}`);
        }
      } else {
        // Multiple documents - go to client page
        nav(`/clients/${clientIdParam}`);
      }
    } else if (response.error) {
      setError(response.error);
      setSaving(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!clientData) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
          <div className="h-6 w-px bg-border" />
          <span className="font-medium text-sm">{currentBusiness?.name}</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-destructive mb-4">{error || 'Client not found'}</p>
            <Button variant="outline" onClick={() => nav('/invoices')}>
              Back to Invoices
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const accountBalance = parseFloat(clientData.account_balance);
  const totalSelected = selectedInvoices.size + selectedQuotes.size;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="flex-1 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-1 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="h-6 w-px bg-border" />
            <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
            <div className="h-6 w-px bg-border" />
            <span className="font-medium text-sm">{currentBusiness?.name}</span>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-8 pb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="max-w-3xl mx-auto space-y-6"
            >
              {/* Title */}
              <h1 className="text-2xl flex items-center gap-2">
                Record Payment
                <DollarSign className="h-5 w-5 text-blue-400" />
              </h1>

              {/* Client Section */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="space-y-2"
              >
                <Label className="text-muted-foreground">Client</Label>
                <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{clientData.client.billing_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                      Account Balance
                    </p>
                    <p className={`text-lg font-semibold ${accountBalance > 0 ? 'text-amber-600' : accountBalance < 0 ? 'text-green-600' : ''}`}>
                      {formatAmount(Math.abs(accountBalance))}
                      {accountBalance < 0 && <span className="text-sm font-normal ml-1">credit</span>}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Outstanding Invoices */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <Label className="text-muted-foreground">Select Invoices to Pay</Label>
                  {totalSelected > 0 && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium">
                        {totalSelected} selected
                      </span>
                      <button
                        type="button"
                        onClick={handleDeselectAll}
                        className="text-sm text-primary hover:underline"
                      >
                        Deselect all
                      </button>
                    </div>
                  )}
                </div>

                {clientData.outstanding_invoices.length === 0 ? (
                  <div className="border rounded-lg p-8 text-center bg-muted/30">
                    <Receipt className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      No outstanding invoices for this client.
                    </p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr className="text-xs text-muted-foreground">
                          <th className="text-left px-4 py-2.5 w-8"></th>
                          <th className="text-left px-3 py-2.5">Invoice</th>
                          <th className="text-left px-3 py-2.5">Status</th>
                          <th className="text-left px-3 py-2.5">Address</th>
                          <th className="text-right px-3 py-2.5">Total</th>
                          <th className="text-right px-3 py-2.5">Balance</th>
                          <th className="text-right px-4 py-2.5 w-36">Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientData.outstanding_invoices.map((invoice) => {
                          const isSelected = selectedInvoices.has(invoice.id);
                          const statusConfig = getInvoiceStatusConfig(invoice.status);

                          return (
                            <tr
                              key={invoice.id}
                              className={`border-t transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/25'}`}
                            >
                              <td className="px-4 py-3">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleInvoiceToggle(invoice.id, invoice)}
                                />
                              </td>
                              <td className="px-3 py-3">
                                <button
                                  type="button"
                                  onClick={() => nav(`/invoices/${invoice.id}`)}
                                  className="text-primary hover:underline font-medium text-sm"
                                >
                                  #{invoice.invoice_number.replace('INV-', '').replace(/^0+/, '') || invoice.invoice_number}
                                </button>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  Due {invoice.due_date
                                    ? format(new Date(invoice.due_date), 'MMM d, yyyy')
                                    : '—'}
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <span className={statusConfig.textColorClass}>
                                  {statusConfig.label}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                                {invoice.client_address || '—'}
                              </td>
                              <td className="px-3 py-3 text-right text-sm">
                                {formatAmount(invoice.total_amount)}
                              </td>
                              <td className="px-3 py-3 text-right font-medium text-sm">
                                {formatAmount(invoice.balance_due)}
                              </td>
                              <td className="px-4 py-3">
                                <CurrencyInput
                                  value={isSelected ? (invoiceAmounts[invoice.id] || '') : ''}
                                  onChange={(value) => handleInvoiceAmountChange(invoice.id, value)}
                                  disabled={!isSelected}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>

              {/* Outstanding Quote Deposits */}
              {quotesWithDeposits.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="space-y-4"
                >
                  <Label className="text-muted-foreground">Select Quotes with Deposits Due</Label>

                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr className="text-xs text-muted-foreground">
                          <th className="text-left px-4 py-2.5 w-8"></th>
                          <th className="text-left px-3 py-2.5">Quote</th>
                          <th className="text-left px-3 py-2.5">Status</th>
                          <th className="text-left px-3 py-2.5">Address</th>
                          <th className="text-right px-3 py-2.5">Deposit</th>
                          <th className="text-right px-3 py-2.5">Collected</th>
                          <th className="text-right px-3 py-2.5">Balance</th>
                          <th className="text-right px-4 py-2.5 w-36">Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quotesWithDeposits.map((quote) => {
                          const isSelected = selectedQuotes.has(quote.id);
                          const statusConfig = getQuoteStatusConfig(quote.status);
                          const depositRequired = parseFloat(quote.deposit);
                          const amountCollected = parseFloat(quote.amount_paid);
                          const balanceDue = parseFloat(quote.balance_due);

                          return (
                            <tr
                              key={quote.id}
                              className={`border-t transition-colors ${isSelected ? 'bg-green-500/5' : 'hover:bg-muted/25'}`}
                            >
                              <td className="px-4 py-3">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleQuoteToggle(quote.id, quote)}
                                />
                              </td>
                              <td className="px-3 py-3">
                                <button
                                  type="button"
                                  onClick={() => nav(`/quotes/${quote.id}`)}
                                  className="text-primary hover:underline font-medium text-sm"
                                >
                                  #{quote.quote_number.replace('QUO-', '').replace(/^0+/, '') || quote.quote_number}
                                </button>
                                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  Quote Deposit
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <span className={statusConfig.textColorClass}>
                                  {statusConfig.label}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                                {quote.client_address || '—'}
                              </td>
                              <td className="px-3 py-3 text-right text-sm">
                                {formatAmount(depositRequired)}
                              </td>
                              <td className="px-3 py-3 text-right text-sm text-muted-foreground">
                                {formatAmount(amountCollected)}
                              </td>
                              <td className="px-3 py-3 text-right font-medium text-sm">
                                {formatAmount(balanceDue)}
                              </td>
                              <td className="px-4 py-3">
                                <CurrencyInput
                                  value={isSelected ? (quoteAmounts[quote.id] || '') : ''}
                                  onChange={(value) => handleQuoteAmountChange(quote.id, value)}
                                  disabled={!isSelected}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}

              {/* Error Message */}
              {error && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-destructive text-sm"
                >
                  {error}
                </motion.p>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Right Sidebar */}
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2, type: 'spring', damping: 25 }}
          className="w-72 border-l bg-muted/30 flex flex-col flex-shrink-0"
        >
          {/* Payment Total Header */}
          <div className="px-5 py-4 border-b">
            <div className="text-sm text-muted-foreground">Payment Total</div>
            <div className="text-2xl font-semibold">
              {formatAmount(totalPaymentAmount)}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pb-5 pt-4 space-y-5">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Payment Details
            </h2>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Payment Method</Label>
              <Select
                value={paymentMethod}
                onValueChange={(value) => setPaymentMethod(value as PaymentMethod)}
              >
                <SelectTrigger className="w-full bg-background">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reference */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Reference #</Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Check #, transaction ID..."
                className="bg-background"
              />
            </div>

            {/* Transaction Date */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Transaction Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-background"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(transactionDate, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={transactionDate}
                    onSelect={(date) => date && setTransactionDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Notes</Label>
              <Textarea
                placeholder="Payment notes..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                className="resize-none bg-background"
              />
            </div>
          </div>

          {/* Fixed action buttons at bottom */}
          <div className="p-4 border-t bg-background space-y-3">
            <Button
              onClick={() => handleSave(true)}
              disabled={saving || totalSelected === 0}
              variant="outline"
              className="w-full"
            >
              Save & Email Receipt
            </Button>
            <Button
              onClick={() => handleSave(false)}
              disabled={saving || totalSelected === 0}
              className="w-full"
            >
              {saving ? 'Saving...' : 'Save Payment'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              disabled={saving}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export function CollectPayment() {
  return <CollectPaymentContent />;
}
